import { Observable, of } from 'rxjs';
import { mergeMap, share } from 'rxjs/operators';
import { LogService } from '../../services/log-service';
import { PusherService } from '../../services/pusher-service';
import { RateViewerPusherActions } from '../../store/rate-viewer/rate-viewer.pusher';
import { ConnectPrinters, ConnectPrinterEvents } from './connect-printers';
import { ConnectScales, ConnectScalesEvents } from './connect-scales';
import { ConnectBatchEvents } from './connect-batch';
import { WorkstationsEvents } from './connect-workstations';
import { BrokerSubject } from './broker-subject';

let instance;

export const shipstationConnect = {
  isInitialized() {
    if (instance) {
      return true;
    }
    return false;
  },

  initialize(observer, userId) {
    if (instance) {
      LogService.logError(new Error('ShipstationConnectService has already been initialized'));
      return;
    }
    if (!observer) {
      LogService.logError(new Error('Must provide observer'));
      return;
    }
    if (!userId) {
      LogService.logError(new Error('Must provide a userId'));
      return;
    }
    // oxlint-disable-next-line @typescript-eslint/no-this-alias
    instance = this;
    this.userId = userId;

    const connectEvents = [
      ...ConnectPrinterEvents,
      ...ConnectScalesEvents,
      ...WorkstationsEvents,
      ...ConnectBatchEvents,
      ...RateViewerPusherActions,
    ];

    const eventHandler = (messageToBind) => (message) => {
      const { shouldIgnoreNotForMe } = messageToBind;
      const messageUserId = message.userId;

      if (shouldIgnoreNotForMe && messageUserId && messageUserId !== userId) {
        return;
      }
      const actions = messageToBind.actionToDispatch(message);
      if (!actions) {
        LogService.logInfo('No action to dispatch Pusher message', {
          messageToBind,
          message,
        });
        return;
      }

      if (Array.isArray(actions)) {
        actions.forEach((action) => observer.next(action));
        return;
      }
      observer.next(actions);
    };
    connectEvents.forEach((messageToBind) => {
      if (messageToBind.persistentTrigger$) {
        PusherService.bindToEventOnAllChannelsWithFallback(
          messageToBind.eventName,
          eventHandler(messageToBind),
          messageToBind.persistentTrigger$,
        );
        return;
      }
      PusherService.bindToEventOnAllChannels(messageToBind.eventName, eventHandler(messageToBind));
    });
  },

  isMessageTargetingUser(message) {
    const { userId } = message;
    return !userId || userId === this.userId;
  },

  readFromScale(scale, requestId) {
    if (!this.isInitialized()) {
      return;
    }
    if (!scale) {
      return;
    }
    ConnectScales.readFromScale(scale, requestId);
  },

  startAutoReadFromScaleWithObserver(sellerId, scale) {
    return new Observable((observer) => {
      if (!this.isInitialized() || !scale || !sellerId) {
        observer.complete();
        return;
      }
      if (!this.autoReadObservers) {
        this.autoReadObservers = {};
      }

      this.autoReadObservers[ConnectScales.autoReadChannelName(sellerId, scale)] = observer;
      ConnectScales.startAutoReadFromScale(sellerId, scale, observer);
    }).pipe(
      mergeMap((next) => of(next)),
      share(),
    );
  },

  startAutoReadFromScaleWithBrokerSubject(sellerId, scale) {
    const subject = new BrokerSubject();

    if (!this.isInitialized() || !scale || !sellerId) {
      subject.complete();
      return;
    }

    if (!this.autoReadObservers) {
      this.autoReadObservers = {};
    }

    this.autoReadObservers[ConnectScales.autoReadChannelName(sellerId, scale)] = subject;

    ConnectScales.startAutoReadFromScale(sellerId, scale, subject);

    return subject.pipe(share());
  },

  startAutoReadFromScale$(sellerId, scale, useReplaySubject = false) {
    if (useReplaySubject) {
      return this.startAutoReadFromScaleWithBrokerSubject(sellerId, scale);
    }

    return this.startAutoReadFromScaleWithObserver(sellerId, scale);
  },

  stopAutoReadFromScale(sellerId, scale) {
    if (!this.isInitialized() || !scale || !sellerId) {
      return;
    }

    const autoReadObserver = this.autoReadObservers?.[ConnectScales.autoReadChannelName(sellerId, scale)];
    if (autoReadObserver) {
      autoReadObserver.complete();
      this.autoReadObservers[ConnectScales.autoReadChannelName(sellerId, scale)] = null;
    }
    ConnectScales.stopAutoReadFromScale(sellerId, scale);
  },

  printDocument(printJob) {
    if (!this.isInitialized()) {
      return;
    }
    return ConnectPrinters.printDocument(printJob);
  },

  isPrinterAvailable(requestedPrinter, connectPrinters = [], workstations = []) {
    // malformed request
    if (!requestedPrinter?.id) {
      return false;
    }

    // printer-level checks:
    const foundPrinter = connectPrinters.find((p) => p.id === requestedPrinter?.id);

    const printerIsNotConnected = requestedPrinter?.id && !foundPrinter;
    const printerIsConnectedButDisabled = foundPrinter?.disabled;

    if (printerIsNotConnected || printerIsConnectedButDisabled) {
      // TODO: DAT - add logs
      return false;
    }

    // it's unlikely that a printer would be available and its associated workstation would be unavailable,
    // but let's make no assumptions. Below we make workstation-levels checks:
    const workstationWithPrinter = workstations?.find((w) => w.workstationId === requestedPrinter?.workstationId);

    const workstationIsNotConnected = !workstationWithPrinter;
    const workstationIsConnectedButOffline = workstationWithPrinter?.version?.split('.')?.[0] >= '4' && !workstationWithPrinter?.isOnline;

    if (workstationIsNotConnected || workstationIsConnectedButOffline) {
      // TODO: DAT - add logs
      return false;
    }

    // printer and workstation are available
    return true;
  },
};
